export type Just<TValue> = Readonly<{
  tag: 'just';
  value: TValue;
}>;

export type Nothing = Readonly<{
  tag: 'nothing';
}>;

export type Maybe<TValue> = Just<TValue> | Nothing;

const NOTHING: Nothing = { tag: 'nothing' };

/** Internal factory — bypasses NonNullable constraint for combinators. */
function mkJust<TValue>(value: TValue): Just<TValue> {
  if (value == null) {
    throw new Error('mkJust: value must not be null or undefined');
  }

  return { tag: 'just', value };
}

export function just<TValue>(
  value: NonNullable<TValue>,
): Just<NonNullable<TValue>> {
  return mkJust(value);
}

export function nothing(): Nothing {
  return NOTHING;
}

export function fromNullable<TValue>(
  value: TValue,
): Maybe<NonNullable<TValue>> {
  return value == null ? nothing() : mkJust(value as NonNullable<TValue>);
}

export function isJust<TValue>(value: Maybe<TValue>): value is Just<TValue> {
  return value.tag === 'just';
}

export function toNullable<TValue>(value: Maybe<TValue>): TValue | null {
  return value.tag === 'just' ? value.value : null;
}

export function toUndefined<TValue>(value: Maybe<TValue>): TValue | undefined {
  return value.tag === 'just' ? value.value : undefined;
}

export const map =
  <TInput, TOutput>(mapper: (value: TInput) => NonNullable<TOutput>) =>
  (value: Maybe<TInput>): Maybe<NonNullable<TOutput>> =>
    value.tag === 'just' ? mkJust(mapper(value.value)) : nothing();

export const flatMap =
  <TInput, TOutput>(mapper: (value: TInput) => Maybe<TOutput>) =>
  (value: Maybe<TInput>): Maybe<TOutput> =>
    value.tag === 'just' ? mapper(value.value) : nothing();

export const getOrElse =
  <TFallback>(fallback: TFallback) =>
  <TValue>(value: Maybe<TValue>): TValue | TFallback =>
    value.tag === 'just' ? value.value : fallback;

export const getOrElseLazy =
  <TFallback>(fallback: () => TFallback) =>
  <TValue>(value: Maybe<TValue>): TValue | TFallback =>
    value.tag === 'just' ? value.value : fallback();

export const getOrThrow =
  (message?: string) =>
  <TValue>(value: Maybe<TValue>): TValue => {
    if (value.tag === 'just') {
      return value.value;
    }

    throw new Error(message ?? 'Expected a value, received nothing');
  };

export const fold =
  <TValue, TResult>(
    onNothing: () => TResult,
    onJust: (value: TValue) => TResult,
  ) =>
  (value: Maybe<TValue>): TResult =>
    value.tag === 'just' ? onJust(value.value) : onNothing();
